import React, { useState } from "react"
import { useStateMachine } from "../../../context/stateMachine/StateMachineContext"
import { Container, FlexContainer, H2 } from "@carrotfertility/carotene"
import { Steps } from "./step-library"
import { Skip } from "../../../views/fle/Molecules"
import { FormInput } from "components/views/atoms/forms/FormInput"
import { Label } from "components/views/atoms/Atoms"
import { useRoutingInfo } from "components/carrot-plans/context/RoutingInfoContext"
import { ValidationMessage } from "components/views/atoms/forms/ValidationMessage"
import ArrowSubmitButton from "components/carrot-plans/shared/ArrowSubmitButton"
import { useJourneyFlow } from "../../shared/JourneyFlowContainer"

interface AgencyClinicStepProps {
  viewName: string
  title: string
  skipped: boolean
  defaultValue?: string | null
  handleUpdate: (value: string) => Promise<void>
  handleSkip: () => Promise<void>
}

function AgencyClinicStep({
  viewName,
  title,
  skipped,
  handleUpdate,
  handleSkip,
  defaultValue
}: AgencyClinicStepProps): JSX.Element {
  const { setNextStep } = useStateMachine(viewName, Boolean(defaultValue) || skipped ? "" : null)
  const [inputValue, setInputValue] = useState(defaultValue)
  const { handleContinueLoading, continueLoading } = useJourneyFlow()
  function sanitizeInputs(inputValue) {
    return inputValue === null || inputValue.split("").every((char) => char === " ") || inputValue === ""
      ? null
      : inputValue
  }
  async function onSkip(): Promise<void> {
    await handleContinueLoading(handleSkip)
    setNextStep(viewName, "")
  }
  async function onSubmit(): Promise<void> {
    const sanitizedInput = sanitizeInputs(inputValue)
    if (sanitizedInput === null) {
      onSkip()
    }
    await handleContinueLoading(() => handleUpdate(sanitizedInput))
    setNextStep(viewName, "")
  }
  return (
    <form
      onSubmit={(event) => {
        event.preventDefault()
        onSubmit()
      }}
    >
      <H2 stack="large">{title}</H2>
      <Container>
        <Container stack="tiny">
          <Label valid={true} htmlFor="agencyClinic">
            Name(s)
          </Label>
        </Container>
        <FormInput id="agencyClinic" value={inputValue} onChange={(event) => setInputValue(event.target.value)} />
        <ValidationMessage errors={[]} name="" />
      </Container>
      <FlexContainer paddingTopBottom="huge" justifyContent="space-between">
        <Skip onClick={onSkip} onClickOnly enabled={!continueLoading} />
        <ArrowSubmitButton enabled={!continueLoading}>Continue</ArrowSubmitButton>
      </FlexContainer>
    </form>
  )
}

function NameOfAgencyStep(): JSX.Element {
  const { updateRoutingInfo, routingData, hasQuestionBeenSkipped } = useRoutingInfo()
  return (
    <AgencyClinicStep
      handleUpdate={(value) => updateRoutingInfo({ agencyAttorneyName: value })}
      handleSkip={() => updateRoutingInfo({ skipFields: ["agencyAttorneyName"] })}
      defaultValue={routingData?.agencyAttorneyName}
      skipped={hasQuestionBeenSkipped("agencyAttorneyName")}
      viewName={Steps.NAME_OF_AGENCY}
      title="What is the name of your agency and/or attorney?"
    />
  )
}

function NameOfClinicAgencyAttorneyStep(): JSX.Element {
  const { updateRoutingInfo, routingData, hasQuestionBeenSkipped } = useRoutingInfo()
  return (
    <AgencyClinicStep
      handleUpdate={(value) => updateRoutingInfo({ clinicAgencyAttorneyName: value })}
      handleSkip={() => updateRoutingInfo({ skipFields: ["clinicAgencyAttorneyName"] })}
      defaultValue={routingData?.clinicAgencyAttorneyName}
      skipped={hasQuestionBeenSkipped("clinicAgencyAttorneyName")}
      viewName={Steps.NAME_OF_CLINIC_AGENCY_ATTORNEY}
      title="What is the name of your clinic, agency/consultancy, and/or attorney?"
    />
  )
}

function NameOfClinicStep(): JSX.Element {
  const { updateRoutingInfo, routingData, hasQuestionBeenSkipped } = useRoutingInfo()
  return (
    <AgencyClinicStep
      handleUpdate={(value) => updateRoutingInfo({ clinicName: value })}
      handleSkip={() => updateRoutingInfo({ skipFields: ["clinicName"] })}
      defaultValue={routingData?.clinicName}
      skipped={hasQuestionBeenSkipped("clinicName")}
      viewName={Steps.NAME_OF_CLINIC}
      title="What is the name of your clinic?"
    />
  )
}
export { NameOfAgencyStep, NameOfClinicAgencyAttorneyStep, NameOfClinicStep }
